import { BookmarkIcon, ChevronDownIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { cn } from "@/lib/utils";
import { CalenderSparkle } from "@/assets/CalenderSparkle";
import { ReviewDecision } from "@/types";

export const SearchV2ShortlistAction = ({
  isAgentActive,
  isShortlisted,
  isShortlisting,
  onShortlist,
  primary = false,
  shortlistedDecision,
  size,
}: {
  isAgentActive: boolean;
  isShortlisted: boolean;
  isShortlisting: boolean;
  onShortlist: (decision: ReviewDecision) => void;
  primary?: boolean;
  shortlistedDecision?: ReviewDecision;
  size: "card" | "table";
}) => {
  /* Helpers */
  const isDisabled = isShortlisted || isShortlisting;
  const isTable = size === "table";
  const isPrimaryAction = primary && !isShortlisted;
  // Candidates shortlisted before this session have no recorded decision, so
  // the clicked style falls back to the current agent toggle.
  const isAgentShortlist =
    shortlistedDecision === ReviewDecision.SHORTLIST ||
    (shortlistedDecision === undefined && isAgentActive);

  /* Handlers */
  const handleShortlist = (decision: ReviewDecision) => {
    onShortlist(decision);
  };

  /* Render */
  const render = () => {
    return (
      <Row
        className={cn(
          "items-center overflow-hidden rounded-md border border-slate-200 bg-white",
          isPrimaryAction && "border-primary-900 bg-primary-900",
          isShortlisted && "border-primary-200 bg-primary-50",
        )}
      >
        <Button
          className={cn(
            isTable ? "h-8 px-2 text-xs [&_svg]:size-3.5" : "h-9 px-3",
            !isShortlisted && !isTable && "[&_svg]:size-5",
            "rounded-none border-0",
            isPrimaryAction &&
              "text-white hover:bg-primary-800 hover:text-white",
            isShortlisted &&
              "text-primary-900 hover:bg-primary-100 hover:text-primary-900",
          )}
          disabled={isDisabled}
          onClick={() => {
            handleShortlist(
              isAgentActive
                ? ReviewDecision.SHORTLIST
                : ReviewDecision.SHORTLIST_MANUAL,
            );
          }}
          type="button"
          variant="ghost"
        >
          {renderShortlistIcon()}
          {isShortlisted ? "Shortlisted" : "Shortlist"}
        </Button>
        {isAgentActive && renderAgentDropdown()}
      </Row>
    );
  };

  const renderShortlistIcon = () => {
    if (isShortlisted && isAgentShortlist) {
      return (
        <CalenderSparkle
          className={isTable ? "h-3.5 w-3.5" : "h-7 w-7"}
          color="hsl(var(--color-primary-900))"
        />
      );
    }
    if (isShortlisted) {
      return (
        <BookmarkIcon
          className={cn(
            isTable ? "h-3.5 w-3.5" : "h-4 w-4",
            "fill-primary-700 text-primary-700",
          )}
        />
      );
    }
    if (!isAgentActive) {
      return (
        <BookmarkIcon
          className={cn(
            isTable ? "h-3.5 w-3.5" : "h-4 w-4",
            isPrimaryAction ? "text-white" : "text-slate-600",
          )}
        />
      );
    }
    return (
      <CalenderSparkle
        backgroundColor={
          isPrimaryAction ? "hsl(var(--color-primary-900))" : undefined
        }
        className={isTable ? "h-3.5 w-3.5" : "h-7 w-7"}
        color={isPrimaryAction ? "#ffffff" : "hsl(var(--color-primary-900))"}
      />
    );
  };

  const renderAgentDropdown = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className={cn(
              isTable ? "h-8" : "h-9",
              "w-7 rounded-none border-0 p-0",
              isPrimaryAction &&
                "border-l border-primary-700 text-white hover:bg-primary-800 hover:text-white",
            )}
            disabled={isDisabled}
            size="icon"
            type="button"
            variant="ghost"
          >
            <ChevronDownIcon className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 p-1.5">
          <DropdownMenuItem
            className="items-start gap-2.5 rounded-md py-2"
            disabled={isDisabled}
            onClick={() => {
              handleShortlist(ReviewDecision.SHORTLIST);
            }}
          >
            <Row className="mt-0.5 h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-100">
              <CalenderSparkle
                className={isTable ? "h-3.5 w-3.5" : "h-4 w-4"}
                color="hsl(var(--color-primary-900))"
              />
            </Row>
            <Column className="gap-0.5">
              <Text className="text-sm font-medium text-slate-800">
                AI scheduling agent
              </Text>
              <Text className="text-xs font-normal leading-snug text-slate-500">
                Reaches out and schedules the candidate automatically.
              </Text>
            </Column>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="items-start gap-2.5 rounded-md py-2"
            disabled={isDisabled}
            onClick={() => {
              handleShortlist(ReviewDecision.SHORTLIST_MANUAL);
            }}
          >
            <Row className="mt-0.5 h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100">
              <BookmarkIcon className="h-3.5 w-3.5 text-slate-600" />
            </Row>
            <Column className="gap-0.5">
              <Text className="text-sm font-medium text-slate-800">
                Manual mode
              </Text>
              <Text className="text-xs font-normal leading-snug text-slate-500">
                You handle the conversation yourself.
              </Text>
            </Column>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return render();
};
